// Inventory & Orders Screen – React + Tailwind (mobile‑first)
// ➡️ Orders screen updated: ingredient items from product are directly added as cost lines (editable), product optional

import React, { useEffect, useState, useRef } from "react";
import { productApi, ordersApi, customersApi, accountsApi, expensesApi } from "../api";
import { getTimeframe, toIsoRange } from "../timeframe";
import InvoiceEditor from "./InvoiceEditor";
import { useI18n } from "../i18n";
import OrderModal from "./OrderModal";
import SubscriptionBanner from "./SubscriptionBanner";
import { useAccount } from "../contexts/AccountContext";
import { usePermissions } from "../contexts/PermissionContext";
import { useOfflineOperations } from "../hooks/useOfflineOperations";
import { showConfirm, showToast } from "../utils/toast";

export default function InventoryAndOrders({ user, subStatus, onSubscribe, onRefreshSubscription }) {
  const { t } = useI18n();
  const { selectedAccountId } = useAccount();
  const { hasPermission } = usePermissions();
  const { canPerformOperation, showOfflineWarning, message: offlineMessage } = useOfflineOperations('orders');
  const [products, setProducts] = useState([]);

  // Customer selection removed

  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showInvoice, setShowInvoice] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);

  const emptyOrder = {
    name: "",
    productId: "",
    date: new Date().toISOString().split("T")[0],
    customLines: [],
    sellingPrice: "",
  };

  const [newOrder, setNewOrder] = useState({ ...emptyOrder });
  const [previewTotal, setPreviewTotal] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [customerQuery, setCustomerQuery] = useState("");
  const [showCustomerList, setShowCustomerList] = useState(false);
  const [showProductList, setShowProductList] = useState(false);
  const [productQuery, setProductQuery] = useState("");
  const [moneyAccounts, setMoneyAccounts] = useState([]);
  const [selectedMoneyAccountId, setSelectedMoneyAccountId] = useState("");
  // Filters
  const [filterQ, setFilterQ] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [incomeCats, setIncomeCats] = useState([]);

  // Keep latest order state for blur handlers to avoid stale closures
  const latestOrderRef = useRef(newOrder);
  useEffect(() => { latestOrderRef.current = newOrder; }, [newOrder]);

  // Ensure selected names are visible in edit mode even if order lacks names
  useEffect(() => {
    if (newOrder.customerId && !customerQuery) {
      try {
        const cname = (customers.find(c => c.customerId === newOrder.customerId) || {}).name || newOrder.customerName || '';
        if (cname) setCustomerQuery(cname);
      } catch {}
    }
  }, [customers, newOrder.customerId, newOrder.customerName, customerQuery]);

  useEffect(() => {
    if (newOrder.productId && !productQuery) {
      try {
        const pname = (products.find(p => String(p.id) === String(newOrder.productId)) || {}).name || '';
        if (pname) setProductQuery(pname);
      } catch {}
    }
  }, [products, newOrder.productId, productQuery]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const prods = await productApi.list();
        setProducts(prods.map(p => ({ id: p.productId, name: p.name, linkedItems: p.linkedItems || [] })));
        const tf = getTimeframe();
        const iso = toIsoRange(tf);
        
        // Prepare order params with business account filtering
        const orderParams = {};
        if (iso.from && iso.to) {
          orderParams.from = iso.from;
          orderParams.to = iso.to;
        }
        if (selectedAccountId) {
          orderParams.accountId = selectedAccountId;
        }
        
        const [ords, custs, moneyAccs, cats] = await Promise.all([
          ordersApi.list(orderParams),
          customersApi.list().catch(() => []),
          accountsApi.list().catch(() => []),
          expensesApi.listUnified('income').catch(()=>({ defaults: ['Sales','Services','Other'], customs: [] })),
        ]);
        setOrders(ords.map(o => ({
          id: o.orderId,
          name: o.name,
          productId: o.productId,
          date: (o.date?._seconds) ? new Date(o.date._seconds * 1000).toISOString().split('T')[0] : emptyOrder.date,
          customLines: o.customLines || [],
          sellingPrice: o.sellingPrice || 0,
          totalCost: o.totalCost || 0,
          productName: '',
          customerId: o.customerId || '',
          customerName: o.customerName || '',
          money_account_id: o.money_account_id || '',
          incomeCategory: o.incomeCategory || '',
        })));
        setCustomers(Array.isArray(custs) ? custs : []);
        setMoneyAccounts(Array.isArray(moneyAccs) ? moneyAccs : []);
        if (Array.isArray(cats)) setIncomeCats(cats);
        else if (cats && typeof cats === 'object') {
          const defs = Array.isArray(cats.defaults) ? cats.defaults : [];
          const customs = Array.isArray(cats.customs) ? cats.customs : [];
          setIncomeCats(Array.from(new Set([ ...defs, ...customs ])));
        }
        try {
          const last = sessionStorage.getItem('tp_selected_account_id') || '';
          if (last && (moneyAccs || []).some(a => a.accountId === last)) setSelectedMoneyAccountId(last);
          else setSelectedMoneyAccountId("");
        } catch { setSelectedMoneyAccountId(""); }
      } catch (e) {
        console.error('Failed to load orders data', e);
      } finally { setIsLoading(false); }
    })();
  }, [selectedAccountId]);

  // React to global timeframe changes
  useEffect(() => {
    const handler = async () => {
      setIsLoading(true);
      try {
        const tf = getTimeframe();
        const iso = toIsoRange(tf);
        const ords = await ((iso.from && iso.to) ? ordersApi.list({ from: iso.from, to: iso.to }) : ordersApi.list());
        setOrders(ords.map(o => ({
          id: o.orderId,
          name: o.name,
          productId: o.productId,
          date: (o.date?._seconds) ? new Date(o.date._seconds * 1000).toISOString().split('T')[0] : emptyOrder.date,
          customLines: o.customLines || [],
          sellingPrice: o.sellingPrice || 0,
          totalCost: o.totalCost || 0,
          productName: '',
          customerId: o.customerId || '',
          customerName: o.customerName || '',
          money_account_id: o.money_account_id || '',
          incomeCategory: o.incomeCategory || '',
        })));
      } catch (e) {
        console.error('Failed to reload orders by timeframe', e);
      } finally { setIsLoading(false); }
    };
    window.addEventListener('TP_TIMEFRAME_CHANGE', handler);
    return () => window.removeEventListener('TP_TIMEFRAME_CHANGE', handler);
  }, []);

  const filteredOrders = React.useMemo(() => {
    const q = String(filterQ || '').trim().toLowerCase();
    const from = filterFrom ? new Date(filterFrom).toISOString() : null;
    const to = filterTo ? new Date(new Date(filterTo).setHours(23,59,59,999)).toISOString() : null;
    // If local range set, filter client-side (we already fetch by global timeframe)
    return orders.filter((o) => {
      const matchesText = !q || String(o.name || '').toLowerCase().includes(q) || String(o.customerName || '').toLowerCase().includes(q) || String(o.incomeCategory || '').toLowerCase().includes(q);
      const matchesCat = !catFilter || String(o.incomeCategory || '').toLowerCase() === String(catFilter||'').toLowerCase();
      if (!from && !to) return matchesText && matchesCat;
      const time = (() => { try { return new Date(o.date).toISOString(); } catch { return null; }})();
      const matchesFrom = from == null || (time != null && time >= from);
      const matchesTo = to == null || (time != null && time <= to);
      return matchesText && matchesCat && matchesFrom && matchesTo;
    });
  }, [orders, filterQ, filterFrom, filterTo, catFilter]);

  const fetchProductIngredientsAsLines = (productId) => {
    const product = products.find((p) => String(p.id) === String(productId));
    if (!product) return [];
    // Inventory usage disabled: only map custom/other items; ignore inventory-linked items
    const lines = (product.linkedItems || [])
      .filter((li) => li.type !== 'inventory')
      .map((li) => ({ name: li.name || "Other", quantity: 1, price: 0, manualPrice: true }));
    return lines;
  };

  const normalizeLines = (lines) => {
    const mapped = (lines || []).map((l) => {
      const qty = parseFloat(l.quantity || 0);
      const normalizedQty = isNaN(qty) || qty <= 0 ? 0 : qty;
      const hasManual = !!l.manualPrice && (l.price !== undefined && l.price !== null && !isNaN(Number(l.price)));
      const priceVal = hasManual ? Number(l.price) : Number(l.price) || 0;
      return { ...l, quantity: normalizedQty, price: priceVal };
    });
    return mapped;
  };

  const handleTotalCost = (lines = newOrder.customLines) => {
    return (lines || []).reduce(
      (sum, line) => sum + (parseFloat(line.price) || 0) * (parseFloat(line.quantity) || 0),
      0
    );
  };

  const handlePlaceOrder = async () => {
    if (isSaving) return;
    try {
      setIsSaving(true);
      const entryPayload = {
        name: newOrder.name,
        productId: newOrder.productId,
        date: newOrder.date,
        customLines: newOrder.customLines,
        sellingPrice: newOrder.sellingPrice,
        customerId: newOrder.customerId || "",
        customerName: (newOrder.customerId ? (customers.find(c=>c.customerId===newOrder.customerId)?.name || '') : (newOrder.customerName || '')).trim(),
        money_account_id: selectedMoneyAccountId || undefined,
      };
      const saved = await ordersApi.create(entryPayload);
      try { if (window && window.gtag) window.gtag('event', 'order_create', { value: Number(newOrder.sellingPrice||0), currency: 'INR' }); } catch {}
      setOrders([{ id: saved.orderId, name: saved.name, productId: saved.productId, date: newOrder.date, customLines: saved.customLines || [], sellingPrice: saved.sellingPrice || 0, totalCost: saved.totalCost || handleTotalCost(), productName: '', incomeCategory: saved.incomeCategory || '' }, ...orders]);
      setNewOrder({ ...emptyOrder });
      setPreviewTotal(0);
      // Refresh subscription status to update usage
      if (onRefreshSubscription) {
        await onRefreshSubscription();
      }
    } catch (e) {
      console.error('Failed to place order', e);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditOrder = (order) => {
    setEditingOrderId(order.id);
    setShowOrderModal(true);
  };

  const handleDeleteOrder = async (order) => {
    if (!order?.id) return;
    const confirmed = await showConfirm({
      title: 'Delete order?',
      message: 'Are you sure you want to delete this order?',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      destructive: true,
      variant: 'warning',
    });
    if (!confirmed) return;
    try {
      await ordersApi.remove(order.id);
      setOrders((prev) => prev.filter((o) => o.id !== order.id));
      setSelectedOrder(null);
      setEditMode(false);
      if (onRefreshSubscription) {
        await onRefreshSubscription();
      }
      showToast({ message: 'Order deleted successfully.', variant: 'success' });
    } catch (e) {
      console.error('Failed to delete order', e);
      showToast({ message: 'Failed to delete order.', variant: 'error' });
    }
  };

  const handleUpdateOrder = async () => {
    if (isSaving) return;
    try {
      setIsSaving(true);
      await ordersApi.update(selectedOrder.id, { 
        ...newOrder,
        money_account_id: selectedAccountId === '' ? null : (selectedAccountId || undefined),
      });
      try { if (window && window.gtag) window.gtag('event', 'order_update', { order_id: selectedOrder.id }); } catch {}
      const nextAccountId = (selectedAccountId === '' ? '' : selectedAccountId);
      const updatedOrders = orders.map((o) =>
        o.id === selectedOrder.id
          ? {
              ...selectedOrder,
              ...newOrder,
              totalCost: handleTotalCost(),
              money_account_id: nextAccountId,
            }
          : o
      );
      setOrders(updatedOrders);
      setEditMode(false);
      setSelectedOrder(null);
      setNewOrder({ ...emptyOrder });
      setPreviewTotal(0);
      // Refresh subscription status to update usage
      if (onRefreshSubscription) {
        await onRefreshSubscription();
      }
    } catch (e) {
      console.error('Failed to update order', e);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setSelectedOrder(null);
    setNewOrder({ ...emptyOrder });
    setPreviewTotal(0);
    try {
      if (typeof window !== 'undefined' && window.scrollTo) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch {}
  };

  const handleProductSelection = async (productId) => {
    const costLines = fetchProductIngredientsAsLines(productId);
    const priced = normalizeLines(costLines);
    setPreviewTotal(handleTotalCost(priced));
    setNewOrder({ ...newOrder, productId, customLines: priced });
    try {
      const pname = (products.find(p => String(p.id) === String(productId)) || {}).name || '';
      setProductQuery(pname);
    } catch {}
  };

  const handleLineChange = async (idx, field, value) => {
    const copy = [...(newOrder.customLines || [])];
    const next = { ...copy[idx], [field]: value };

    if (field === 'price') {
      next.manualPrice = true;
      copy[idx] = next;
      const localTotal = handleTotalCost(copy);
      setPreviewTotal(localTotal);
      setNewOrder({ ...newOrder, customLines: copy });
      return;
    }

    copy[idx] = next;
    if (field === 'quantity') {
      const priced = normalizeLines(copy);
      setPreviewTotal(handleTotalCost(priced));
      setNewOrder({ ...newOrder, customLines: priced });
    } else {
      setPreviewTotal(handleTotalCost(copy));
      setNewOrder({ ...newOrder, customLines: copy });
    }
  };

  useEffect(() => {
    const start = Date.now();
    try { if (window && window.gtag) window.gtag('event', 'engagement_begin', { screen_name: 'orders' }); } catch {}
    return () => {
      try { if (window && window.gtag) window.gtag('event', 'engagement_time', { screen_name: 'orders', value: Date.now() - start }); } catch {}
    };
  }, []);

  const currentCost = (previewTotal || handleTotalCost());
  const currentSelling = isNaN(parseFloat(newOrder.sellingPrice)) ? 0 : parseFloat(newOrder.sellingPrice);
  const currentProfit = (currentSelling || 0) - (currentCost || 0);

  // Also scroll to top whenever edit mode turns on, as a safety across devices
  useEffect(() => {
    if (!editMode) return;
    try {
      if (typeof window !== 'undefined' && window.scrollTo) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch {}
  }, [editMode]);

  return (
    <div className="min-h-screen bg-white text-gray-900 p-4 font-sans">
      {/* Offline Warning Banner */}
      {showOfflineWarning && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-yellow-600">⚠️</span>
            <div>
              <div className="font-medium text-yellow-800">Offline Mode</div>
              <div className="text-sm text-yellow-700">{offlineMessage}</div>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">{t('orders.title')}</h1>
        {hasPermission('orders:create') && (
          <button 
            data-tour="add-order" 
            className="text-sm bg-green-600 text-white px-3 py-2 rounded-full shadow"
            onClick={()=>{ 
              // Check if subscription is inactive
              if (subStatus?.valid === false) {
                onSubscribe?.();
                return;
              }
              // Check if trial user has exceeded quota
              if (subStatus?.plan === 'trial' && subStatus?.quotaInfo?.quotaExceeded) {
                onSubscribe?.();
                return;
              }
              setEditingOrderId(null); 
              setShowOrderModal(true); 
            }}
          >
            + {t('orders.form.newTitle')}
          </button>
        )}
      </div>

      <SubscriptionBanner 
        user={user} 
        subStatus={subStatus} 
        onSubscribe={onSubscribe}
        showCreateButton={true}
        quotaInfo={subStatus?.quotaInfo}
      />


      {/* Filters */}
      <div className="bg-white border border-green-100 rounded-2xl p-3 shadow-sm mb-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 items-start">
          <div className="col-span-1 sm:col-span-2 md:col-span-2 min-w-0">
            <div className="text-[11px] text-gray-600 mb-1 h-[14px]">{t('orders.filter.search.placeholder')}</div>
            <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔎</span>
            <input
              className="w-full border rounded-full pl-8 pr-10 py-2 text-sm bg-green-50/60 focus:bg-white"
              placeholder={t('orders.filter.search.placeholder')}
              value={filterQ}
              onChange={(e)=> setFilterQ(e.target.value)}
              aria-label={t('orders.filter.search.placeholder')}
            />
          </div>
          </div>
          <div className="min-w-0">
            <div className="text-[11px] text-gray-600 mb-1 h-[14px]">{t('orders.filter.from')}</div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">📅</span>
              <input
                type="date"
                className="w-full border rounded-full pl-8 pr-3 py-2 text-sm bg-green-50/60 focus:bg-white"
                value={filterFrom}
                onChange={(e)=> { const v = e.target.value; setFilterFrom(v); }}
                aria-label={t('orders.filter.from')}
              />
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-[11px] text-gray-600 mb-1 h-[14px]">{t('orders.filter.to')}</div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">📅</span>
              <input
                type="date"
                className="w-full border rounded-full pl-8 pr-3 py-2 text-sm bg-green-50/60 focus:bg-white"
                value={filterTo}
                onChange={(e)=> { const v = e.target.value; setFilterTo(v); }}
                aria-label={t('orders.filter.to')}
              />
            </div>
          </div>
          <div className="sm:col-span-2 md:col-span-1 min-w-0 flex flex-col justify-end">
            <div className="text-[11px] text-transparent mb-1 h-[14px]">&nbsp;</div>
            <div className="flex gap-2">
              <select className="flex-1 border rounded-full px-3 py-2 text-sm bg-green-50/60 focus:bg-white text-gray-700" value={catFilter} onChange={(e)=> setCatFilter(e.target.value)}>
                <option value="">All Categories</option>
                {incomeCats.map(c => (<option key={c} value={c}>{c}</option>))}
              </select>
              <button
                type="button"
                className="w-full sm:w-auto text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full px-3 py-2"
                onClick={() => { setFilterQ(""); setFilterFrom(""); setFilterTo(""); setCatFilter(""); }}
                aria-label={t('orders.filter.clear')}
                title={t('orders.filter.clear')}
              >
                <span className="mr-1">✕</span>{t('orders.filter.clear')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <h2 className="text-base font-semibold mb-2">{t('orders.recent.title')}</h2>
      {/* Range pill */}
      <div className="mb-2">
        {(() => { try { const tf = getTimeframe(); const fromDisp = filterFrom || tf.from; const toDisp = filterTo || tf.to; return (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 border border-green-100 shadow-sm text-[11px] text-gray-700">
            <span>Range:</span>
            <span className="font-semibold">{fromDisp} → {toDisp}</span>
          </div>
        ); } catch { return null; } })()}
      </div>
      {isLoading ? (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <div className="h-5 w-5 rounded-full border-4 border-green-600 border-t-transparent animate-spin"></div>
          <span>{t('orders.recent.loading')}</span>
        </div>
      ) : (
        filteredOrders.length === 0 && (
          <div className="flex items-center justify-between bg-green-50 border border-green-100 rounded-xl p-3">
            <p className="text-sm text-gray-700">{t('orders.recent.empty')}</p>
            <button 
              data-tour="add-order" 
              className="text-sm bg-green-600 text-white px-3 py-2 rounded-full shadow" 
              onClick={()=>{ 
                // Check if subscription is inactive
                if (subStatus?.valid === false) {
                  onSubscribe?.();
                  return;
                }
                // Check if trial user has exceeded quota
                if (subStatus?.plan === 'trial' && subStatus?.quotaInfo?.quotaExceeded) {
                  onSubscribe?.();
                  return;
                }
                setEditingOrderId(null); 
                setShowOrderModal(true); 
              }}
            >
              + {t('orders.form.newTitle')}
            </button>
          </div>
        )
      )}
      <ul className="space-y-2">
        {!isLoading && filteredOrders.map((order) => {
          const profit = (Number(order.sellingPrice || 0) - Number(order.totalCost || 0));
          const ok = profit >= 0;
          return (
            <li key={order.id} className="border rounded-2xl bg-white p-3 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{order.name || order.productName || t('orders.recent.customOrder')}</div>
                  <div className="text-xs text-gray-500 mt-0.5 truncate">{order.date}</div>
                  <div className="flex items-center gap-3 mt-1 text-xs">
                    <span className="text-gray-700">{t('orders.card.labels.selling')}: <span className="font-semibold">₹{Number(order.sellingPrice || 0).toFixed(0)}</span></span>
                    <span className="text-gray-700">{t('orders.card.labels.cost')}: <span className="font-semibold">₹{Number(order.totalCost || 0).toFixed(0)}</span></span>
                    <span className={ok ? 'text-green-700' : 'text-red-600'}>{t('orders.card.labels.profit')}: <span className="font-semibold">₹{profit.toFixed(0)}</span></span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    className="text-xs bg-white text-green-700 border border-green-200 px-3 py-1.5 rounded-full"
                    onClick={() => { setSelectedOrder(order); setShowInvoice(true); }}
                    disabled={isSaving}
                  >
                    Invoice
                  </button>
                  {hasPermission('orders:edit') && (
                    <button
                      className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-full shadow"
                      onClick={() => handleEditOrder(order)}
                      disabled={isSaving}
                    >
                      {t('orders.recent.edit')}
                    </button>
                  )}
                  {hasPermission('orders:delete') && (
                    <button
                      className="text-xs bg-red-50 text-red-700 border border-red-200 px-3 py-1.5 rounded-full"
                      onClick={() => handleDeleteOrder(order)}
                      disabled={isSaving}
                    >
                      {t('orders.recent.delete')}
                    </button>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <InvoiceEditor
        open={!!showInvoice}
        onClose={() => setShowInvoice(false)}
        order={selectedOrder}
        defaultProductName={(() => { try { return (products.find(p=>String(p.id)===String(selectedOrder?.productId))||{}).name || ''; } catch { return ''; } })()}
      />

      <OrderModal open={!!showOrderModal} orderId={editingOrderId} onClose={async (changed)=>{
        setShowOrderModal(false);
        setEditingOrderId(null);
        if (changed) {
          try {
            const ords = await ordersApi.list();
            setOrders(ords.map(o => ({
              id: o.orderId,
              name: o.name,
              productId: o.productId,
              date: (o.date?._seconds) ? new Date(o.date._seconds * 1000).toISOString().split('T')[0] : emptyOrder.date,
              customLines: o.customLines || [],
              sellingPrice: o.sellingPrice || 0,
              totalCost: o.totalCost || 0,
              productName: '',
              customerId: o.customerId || '',
              customerName: o.customerName || '',
              money_account_id: o.money_account_id || '',
              incomeCategory: o.incomeCategory || '',
            })));
            // Refresh subscription status to update usage
            if (onRefreshSubscription) {
              await onRefreshSubscription();
            }
          } catch {}
        }
      }} />

      {isSaving && (
        <div className="fixed inset-0 z-50 bg-white/70 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white border border-green-200 rounded-2xl px-5 py-4 shadow-md flex items-center">
            <div className="h-6 w-6 mr-3 rounded-full border-4 border-green-600 border-t-transparent animate-spin"></div>
            <div className="text-sm font-semibold text-green-700">{t('orders.form.saving')}</div>
          </div>
        </div>
      )}
    </div>
  );
}
